import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Quote } from 'lucide-react';
import { testimonials } from './testimonialsData';

export default function TestimonialSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000); // Increased from 6000 to 8000 for slower transitions
    return () => clearInterval(timer);
  }, []);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <div className="relative">
      <div className="flex justify-center mb-4">
        <div className="bg-white/10 rounded-full p-2">
          <Quote className="h-5 w-5 text-white" />
        </div>
      </div>

      <h3 className="text-lg font-semibold text-white mb-6 text-center">
        What Our Partners Say
      </h3>

      <div className="relative min-h-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.9 }} // Increased from 0.5 to 0.8 for smoother transition
            className="flex flex-col items-center"
          >
            {/* Profile Image - Increased size */}
            <div className="relative w-24 h-24 mb-4"> {/* Increased from w-20 h-20 to w-24 h-24 */}
              <img
                src={testimonials[currentIndex].image}
                alt={testimonials[currentIndex].author}
                className="w-full h-full rounded-full object-cover border-2 border-white shadow-lg"
                style={{ imageRendering: 'crisp-edges' }}
              />
              <div className="absolute inset-0 rounded-full ring-1 ring-green-300 ring-offset-2 ring-offset-green-900" />
            </div>

            {/* Quote */}
            <blockquote className="text-white/90 italic mb-4 text-center text-sm">
              "{testimonials[currentIndex].quote}"
            </blockquote>

            {/* Author Info */}
            <div className="text-center">
              <p className="font-semibold text-white text-base">{testimonials[currentIndex].author}</p>
              <p className="text-green-300 text-sm">{testimonials[currentIndex].title}</p>
              <p className="text-white/75 text-xs">{testimonials[currentIndex].organization}</p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-1">
          <button
            onClick={handlePrevious}
            className="p-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Previous testimonial"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={handleNext}
            className="p-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Next testimonial"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Indicators */}
      <div className="flex justify-center mt-4 space-x-1">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-white w-4' : 'bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}